import React, { useState } from 'react';
import { Package } from 'lucide-react';
import { iconMap } from '../shared/icons';

const Products = ({ theme, categories, businessSlug = '', businessName = '', onAddToCart }) => {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  const withProducts = categories.filter(cat => cat.products && cat.products.length > 0);
  if (!withProducts.length) return null;

  return (
    <>
      <section style={{
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.shadows.md,
        padding: '40px',
        marginBottom: '40px'
      }}>
        <h2 style={{
          fontFamily: theme.fonts.heading, color: theme.colors.heading,
          fontSize: '2rem', margin: '0 0 30px 0', textAlign: 'center'
        }}>
          Products
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {withProducts.map((category, index) => {
            const isExpanded = expandedCategory === category.name;
            return (
              <div key={index}>
                <button
                  onClick={() => setExpandedCategory(isExpanded ? null : category.name)}
                  style={{
                    width: '100%',
                    backgroundColor: isExpanded ? theme.colors.primary : theme.colors.surface,
                    color: isExpanded ? '#ffffff' : theme.colors.heading,
                    padding: '20px 25px',
                    borderRadius: theme.borderRadius.md,
                    border: `2px solid ${isExpanded ? theme.colors.primary : theme.colors.border}`,
                    fontSize: '1.3rem', fontFamily: theme.fonts.heading, fontWeight: '600',
                    cursor: 'pointer', display: 'flex', alignItems: 'center',
                    justifyContent: 'space-between', transition: 'all 0.3s ease'
                  }}
                  onMouseOver={(e) => {
                    if (!isExpanded) {
                      e.currentTarget.style.borderColor = theme.colors.primary;
                      e.currentTarget.style.backgroundColor = theme.colors.surface;
                    }
                  }}
                  onMouseOut={(e) => {
                    if (!isExpanded) {
                      e.currentTarget.style.borderColor = theme.colors.border;
                      e.currentTarget.style.backgroundColor = theme.colors.surface;
                    }
                  }}
                >
                  <span>{category.name}</span>
                  <span style={{ fontSize: '1.5rem', transition: 'transform 0.3s ease', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                </button>

                <div style={{
                  maxHeight: isExpanded ? '5000px' : '0',
                  overflow: 'hidden',
                  transition: 'max-height 0.5s ease-in-out',
                  opacity: isExpanded ? 1 : 0,
                  visibility: isExpanded ? 'visible' : 'hidden'
                }}>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                    gap: '20px', padding: '20px 0'
                  }}>
                    {category.products.map((product, pIndex) => (
                      <div
                        key={pIndex}
                        data-category={category.name}
                        data-product-index={pIndex}
                        style={{
                          backgroundColor: theme.colors.surface,
                          borderRadius: theme.borderRadius.sm,
                          border: `1px solid ${theme.colors.border}`,
                          overflow: 'hidden',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px)';
                          e.currentTarget.style.boxShadow = theme.shadows.md;
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                        onClick={() => setLightboxImage(product.image)}
                      >
                        <img
                          key={product.image}
                          data-field="product-image"
                          data-category={category.name}
                          data-product-index={pIndex}
                          src={product.image}
                          alt={product.name}
                          style={{ width: '100%', height: '200px', objectFit: 'cover', cursor: 'pointer' }}
                        />
                        <div style={{ padding: '15px' }}>
                          <h3
                            data-field="product-name"
                            data-category={category.name}
                            data-product-index={pIndex}
                            style={{
                              fontFamily: theme.fonts.heading, color: theme.colors.heading,
                              fontSize: '1rem', margin: '0 0 8px 0'
                            }}
                          >
                            {product.name}
                          </h3>
                          {product.price && (
                            <p
                              data-field="product-price"
                              data-category={category.name}
                              data-product-index={pIndex}
                              style={{
                                fontFamily: theme.fonts.body, color: theme.colors.primary,
                                fontSize: '1.1rem', fontWeight: '600', margin: '0'
                              }}
                            >
                              {product.price}
                            </p>
                          )}
                          {product.id && onAddToCart && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onAddToCart({
                                  productId: product.id,
                                  name: product.name,
                                  price: parseFloat(String(product.price || '').replace(/[^0-9.]/g, '')) || 0,
                                  image: product.image,
                                  vendorSlug: businessSlug,
                                  vendorName: businessName,
                                });
                              }}
                              style={{
                                marginTop: '12px', width: '100%', padding: '8px',
                                backgroundColor: theme.colors.primary, color: '#fff',
                                border: 'none', borderRadius: theme.borderRadius.sm,
                                cursor: 'pointer', fontSize: '0.9rem', fontWeight: 600
                              }}
                            >
                              Add to cart
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {lightboxImage && (
        <div
          onClick={() => setLightboxImage(null)}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.9)', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            zIndex: 9999, cursor: 'pointer', padding: '20px'
          }}
        >
          <img
            src={lightboxImage}
            alt="Product"
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: '95%', maxHeight: '95%', objectFit: 'contain',
              borderRadius: '8px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)'
            }}
          />
          <button
            onClick={() => setLightboxImage(null)}
            style={{
              position: 'absolute', top: '20px', right: '20px',
              background: 'rgba(255,255,255,0.9)', border: 'none',
              borderRadius: '50%', width: '40px', height: '40px',
              fontSize: '24px', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              fontWeight: 'bold', color: '#333'
            }}
          >
            ×
          </button>
        </div>
      )}
    </>
  );
};

export default Products;
